import {
    type AccountType,
    integrations,
    type ListingStatuses,
    originalChannels,
    providerChannels
} from "@hosttools/core/constant";
import classNames from "classnames";
import {memo, useCallback, useMemo} from "react";

import {useStatusLabel} from "../../hooks";
import {buildChannel, isICalendarObject} from "../../models/channel";
import {CloseIcon, useChannel} from "../../react";
import {Box} from "../ui/box";
import {Center} from "../ui/center";
import {HStack} from "../ui/hstack";
import {Image} from "../ui/image";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";
import {Tooltip} from "../ui/tooltip";

import Dot from "./Dot";
import type {Size} from "./useChannelLogoVariant";
import {useChannelLogoVariant} from "./useChannelLogoVariant";

export type Props = {
    size?: Size;
    includeLabel?: boolean;
    status?: ListingStatuses;
    isChannelCC?: boolean;
    className?: string;
    onPress?: (channel: string) => void;
} & StrictUnion<
    | {
          channel: AccountType | "internal" | "direct" | "icalendar";
          onRemove?: (channel: AccountType) => void;
      }
    | {
          channel: string;
          logoUrl: string;
          onRemove?: (channel: string) => void;
      }
>;

const ChannelLogo: React.FC<Props> = ({
    channel,
    size = "sm",
    includeLabel,
    status,
    logoUrl,
    isChannelCC,
    className,
    onPress,
    onRemove
}) => {
    const channelConfig = useChannel(channel, isChannelCC);
    const value = useChannelLogoVariant(size);
    const statusLabel = useStatusLabel(status);

    const logo = useMemo(() => {
        // ignore the circle variant for internal and direct channels
        if (channel === "internal" || channel === "direct") {
            const channelConfig = buildChannel(channel);
            const {LogoIcon, label} = channelConfig;
            if (LogoIcon) {
                return (
                    <HStack space={value.space}>
                        <LogoIcon
                            className={value.logoSizeClassName}
                            data-testid={channel}
                            fill={channel}
                        />
                        {includeLabel && <Text variant={value.textSize}>{label}</Text>}
                    </HStack>
                );
            }
        }

        if (providerChannels.includes(channel) || integrations.includes(channel)) {
            const channelConfig = buildChannel(channel);

            const {LogoIcon, label} = channelConfig;
            if (LogoIcon) {
                return (
                    <HStack space={value.space} testID="logo-icon-wrapper">
                        <LogoIcon className={value.logoSizeClassName} data-testid="logo-icon" />
                        {includeLabel && <Text variant={value.textSize}>{label}</Text>}
                    </HStack>
                );
            }
        }

        if (isICalendarObject(channelConfig)) {
            // ignore the circle variant for icalendar
            const {Logo, label} = channelConfig;
            if (Logo) {
                return (
                    <HStack space={value.space}>
                        <Logo
                            className={`${value.logoSizeClassName} fill-icalendar-text`}
                            data-testid={channel}
                        />
                        {includeLabel && <Text variant={value.textSize}>{label}</Text>}
                    </HStack>
                );
            }
        }

        const imageSource = {uri: logoUrl};
        return <Image alt={channel} className={value.logoSizeClassName} source={imageSource} />;
    }, [
        channel,
        logoUrl,
        includeLabel,
        value.logoSizeClassName,
        channelConfig,
        value.space,
        value.textSize
    ]);

    const handleRemove = useCallback(() => {
        if (originalChannels.includes(channel)) {
            onRemove?.(channel);
        }

        if (logoUrl) {
            onRemove?.(channel);
        }
    }, [channel, logoUrl, onRemove]);

    const handlePress = useCallback(() => {
        onPress?.(channel);
    }, [onPress, channel]);

    return (
        <Box className={classNames("relative", className)} testID="channel-logo-wrapper">
            {onPress ? (
                <Tooltip
                    label={`${channelConfig.label}${statusLabel ? `: ${statusLabel}` : ""}`}
                    onPress={handlePress}
                >
                    {logo}
                </Tooltip>
            ) : (
                <Box className="pointer-events-none">
                    <Tooltip
                        label={`${channelConfig.label}${statusLabel ? `: ${statusLabel}` : ""}`}
                    >
                        {logo}
                    </Tooltip>
                </Box>
            )}
            {channel !== "internal" && channel !== "direct" && onRemove && (
                <Center
                    className={`
                        absolute
                        z-10
                        p-1
                        rounded-full
                        -bottom-2
                        -right-2
                        border
                        border-white
                        bg-blueGray-500
                    `}
                >
                    <Tooltip
                        label={logoUrl ? `Unlist on ${channel}` : "Remove channel"}
                        // TODO: wrap this as child of `Pressable` won't work with `onPress` callback
                    >
                        <Pressable onPress={handleRemove}>
                            <CloseIcon fill="white" size="3xs" />
                        </Pressable>
                    </Tooltip>
                </Center>
            )}
            {typeof status !== "undefined" && (
                <>
                    {status === "listed" ? (
                        <Dot className="absolute top-0 right-0 bg-success-600" />
                    ) : (
                        <Dot className="absolute top-0 right-0 bg-blueGray-400" />
                    )}
                </>
            )}
        </Box>
    );
};

export default memo(ChannelLogo);
